/**
 * Centralized Session Management API
 * 
 * This module provides endpoints that can be called by other applications
 * to check session status and manage authentication across subdomains.
 */

import Cookies from 'js-cookie';
import { ENV } from '../config/env';

const getCookieDomain = () => {
  const hostname = window.location.hostname;
  if (hostname.includes('deepsearchz.com')) {
    return '.deepsearchz.com';
  }
  return undefined;
};

/**
 * Get session data from localStorage and cookies
 */
export const getLocalSession = () => {
  try {
    let userLS = localStorage.getItem('ds_user');
    let profileLS = localStorage.getItem('ds_profile');
    
    if (userLS && profileLS) {
      return {
        authenticated: true,
        user: JSON.parse(userLS),
        profile: JSON.parse(profileLS),
        source: 'localStorage'
      };
    }

    const userCookie = Cookies.get('ds_user');
    const profileCookie = Cookies.get('ds_profile');
    
    if (userCookie && profileCookie) {
      localStorage.setItem('ds_user', userCookie);
      localStorage.setItem('ds_profile', profileCookie);
      
      return {
        authenticated: true,
        user: JSON.parse(userCookie),
        profile: JSON.parse(profileCookie),
        source: 'cookie'
      };
    }

    return {
      authenticated: false,
      user: null,
      profile: null,
      source: 'none'
    };
  } catch (error) {
    console.error('[SessionAPI] Error reading local session:', error);
    return {
      authenticated: false,
      user: null,
      profile: null,
      error: error.message
    };
  }
};

/**
 * Get the current user object directly
 */
export const getCurrentUser = () => {
  const session = getLocalSession();
  return session.user;
};

/**
 * Check if user is authenticated
 */
export const checkSession = async () => {
  try {
    const localSession = getLocalSession();
    
    if (localSession.authenticated) {
      return {
        success: true,
        authenticated: true,
        user: localSession.user,
        profile: localSession.profile,
        source: localSession.source
      };
    }

    return {
      success: true,
      authenticated: false,
      user: null,
      profile: null,
      source: 'none'
    };
  } catch (error) {
    console.error('[SessionAPI] Error checking session:', error);
    return {
      success: false,
      authenticated: false,
      error: error.message
    };
  }
};

/**
 * Logout and clear all session data
 */
export const logoutAll = async () => {
  try {
    // Call NoCodeBackend sign-out API
    await fetch(`${ENV.NCB_AUTH_API}/sign-out`, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${ENV.NCB_SECRET_KEY}`,
        "X-Database-Instance": ENV.NCB_INSTANCE,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({}),
      credentials: 'include'
    });
  } catch (error) {
    console.error('[SessionAPI] Error calling sign-out API:', error);
  }

  localStorage.removeItem('ds_user');
  localStorage.removeItem('ds_profile');

  const domain = getCookieDomain();
  const cookiesToClear = [
    'ds_user',
    'ds_profile',
    'better-auth.session-token',
    '__Secure-better-auth.session-token',
    '__Host-better-auth.session-token'
  ];

  cookiesToClear.forEach(cookieName => {
    try {
      Cookies.remove(cookieName, { domain });
      Cookies.remove(cookieName);
      Cookies.remove(cookieName, { path: '/' });
      Cookies.remove(cookieName, { domain, path: '/' });
    } catch (error) {
      console.error(`[SessionAPI] Error removing cookie ${cookieName}:`, error);
    }
  });

  return {
    success: true,
    message: 'All sessions cleared'
  };
};

export const syncSession = () => {
  window.addEventListener('storage', (e) => {
    if (e.key === 'ds_user' || e.key === 'ds_profile') {
      window.dispatchEvent(new CustomEvent('session-changed', {
        detail: {
          key: e.key,
          oldValue: e.oldValue,
          newValue: e.newValue
        }
      }));
    }
  });
};

export const exportSession = () => {
  const session = getLocalSession();
  return {
    authenticated: session.authenticated,
    user: session.user,
    profile: session.profile,
    timestamp: new Date().toISOString()
  };
};